/**
 * mobile-tab-bar.tsx — Thanh tab dưới của bản điện thoại (kiểu Lark Mobile), thay cho cột menu trái:
 *   Nhắn tin (chấm đếm chưa đọc) · Khách hàng · Báo cáo · Thông báo (chuông) · Thêm (hộp trượt 2/3 màn: AI hỗ trợ + Ứng dụng).
 * Chừa vùng an toàn dưới (thanh Home iPhone). Mục nào vai trò không có quyền thì vắng (xem splitMobileNav).
 */
import { useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Grid2x2 } from 'lucide-react'
import { navForRole, isSectionActive, type NavItem } from './nav-config'
import { NotificationsBell } from './notifications-bell'
import { MobileMoreSheet } from './mobile-more-sheet'
import { useAuthStore } from '@/stores/auth-store'
import { useConversationCounts } from '@/hooks/use-conversations'
import { splitMobileNav } from '@/lib/mobile-nav'
import { mobileNav } from '@/lib/mobile-scope'
import { cn } from '@/lib/utils'
import { useInboxScrollStore } from '@/pages/conversations/inbox-scroll-store'
import { useTranslation } from 'react-i18next'

const TAB = 'relative flex h-14 flex-1 flex-col items-center justify-center gap-0.5 text-[10px] leading-none transition-colors'
const ACTIVE = 'font-medium text-primary'
const IDLE = 'text-muted-foreground active:text-foreground'

export function MobileTabBar() {
  const { pathname } = useLocation()
  const role = useAuthStore((s) => s.user?.role)
  const { t, i18n } = useTranslation('shell')
  // Nhãn tab dịch lúc dựng → dựng lại khi đổi ngôn ngữ.
  const nav = useMemo(() => mobileNav(navForRole(role)), [role, i18n.language])
  const { tabs } = useMemo(() => splitMobileNav(nav), [nav])
  const { data: counts } = useConversationCounts()
  const requestInboxReset = useInboxScrollStore((s) => s.requestReset)
  const [moreOpen, setMoreOpen] = useState(false)
  // "Thêm" sáng lên khi đang ở trang mở từ hộp đó: ứng dụng khác (kể cả trang chỉ dùng trên máy tính), tài khoản, gói dịch vụ…
  const moreActive = !tabs.some((t) => isSectionActive(t, pathname))

  return (
    <>
      <nav aria-label={t('mobile.mainNav')} className="z-30 flex shrink-0 border-t bg-background pb-safe pl-safe pr-safe">
        {tabs.map((item) => {
          const active = isSectionActive(item, pathname)
          return (
            <TabLink
              key={item.key}
              item={item}
              active={active}
              badge={item.key === 'inbox' ? counts?.unread ?? 0 : 0}
              // Bấm lại tab "Nhắn tin" trong lúc ĐANG đứng đúng màn danh sách đó (tab bar chỉ hiện ở màn danh
              // sách, không hiện trong khung chat — app-layout.tsx) → về đầu danh sách, kiểu Zalo.
              onClick={item.key === 'inbox' && active ? requestInboxReset : undefined}
            />
          )
        })}
        <NotificationsBell
          side="top"
          align="center"
          badgeClassName="left-1/2 top-1.5 ml-1"
          className={cn(TAB, IDLE, 'h-14 rounded-none px-0 font-normal hover:bg-transparent [&_svg]:size-6')}
          label={<span className="text-[10px] leading-none">{t('rail.notifications')}</span>}
        />
        <button
          type="button"
          onClick={() => setMoreOpen(true)}
          aria-label={t('more.title')}
          aria-haspopup="dialog"
          className={cn(TAB, moreActive ? ACTIVE : IDLE)}
        >
          {/* Chấm "còn việc ở trang Bắt đầu" nay nằm trên ảnh đại diện đầu Nhắn tin (mục Bắt đầu ở hộp Cá nhân). */}
          <Grid2x2 className="h-6 w-6" strokeWidth={moreActive ? 2.2 : 1.8} />
          {t('more.title')}
        </button>
      </nav>
      <MobileMoreSheet open={moreOpen} onOpenChange={setMoreOpen} />
    </>
  )
}

function TabLink({ item, active, badge, onClick }: { item: NavItem; active: boolean; badge: number; onClick?: () => void }) {
  const Icon = item.icon
  return (
    <Link to={item.to} onClick={onClick} aria-current={active ? 'page' : undefined} className={cn(TAB, active ? ACTIVE : IDLE)}>
      <span className="relative">
        <Icon className="h-6 w-6" strokeWidth={active ? 2.2 : 1.8} />
        {badge > 0 && (
          <span className="absolute -right-2.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-bold leading-none text-destructive-foreground">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </span>
      {item.label}
    </Link>
  )
}
